import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {
  buildContactsManagerAccountSyncsUrl,
  buildContactsManagerSyncsUrl,
} from '../../../../../../helpers/contactsManagerHelpers';
import {useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {CrmSyncSourcesEnum} from '../../../../../../models/api/crmSyncModel';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {getHasCrmSyncFullPageSalesforce} from '../../../../../../store/featureFlags/featureFlagsSelectors';

/*
 * Constants.
 */

const seeHowItWorksLink =
  'http://help.frontapp.com/t/636nhg/how-to-enable-and-use-the-salesforce-integration';

/*
 * Props.
 */

interface SettingsAppsConfigureSalesforceProps {
  username?: string;
  onSignOut: Handler;
  onSignIn: HandlerOf<boolean>;
}

/*
 * Style.
 */

const StyledSectionDiv = styled.div`
  margin-bottom: 8px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 4px;
`;

const StyledUsernameSpan = styled.span`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledHr = styled.hr`
  margin: 20px 0 12px 0;
  border: none;
  border-top: 1px solid ${(p) => p.theme.greys.shade40};
`;

const StyledAccountSyncTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  margin-bottom: 4px;
`;

const StyledAccountSyncContentWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 12px;
  align-items: center;
`;

const StyledAccountSyncDescriptionDiv = styled.div`
  flex: 1;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledManageButtonDiv = styled.div`
  display: flex;
  height: 30px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureSalesforce: FC<SettingsAppsConfigureSalesforceProps> = (props) => {
  const {username, onSignOut, onSignIn} = props;
  const {navigateToLink} = useLinkNavigation();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const hasCrmSyncFullPageSalesforce = useAppSelector(getHasCrmSyncFullPageSalesforce);

  const onNavigateToAccountSync = () => {
    if (hasCrmSyncFullPageSalesforce) {
      navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.SALESFORCE));
    } else {
      navigateToLink(buildContactsManagerAccountSyncsUrl(CrmSyncSourcesEnum.SALESFORCE));
    }
  };

  return (
    <StyledSectionDiv>
      <Trans id="settings-apps-configure-app-dialog-salesforce-display">
        Displays Salesforce information alongside your conversations.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-salesforce-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {maybeRenderUnlinkContent(username, isGlobalAdmin, onSignOut)}
      {maybeRenderLinkContent(username, isGlobalAdmin, onSignIn)}
      <StyledHr />
      {renderAccountSyncDetails(isGlobalAdmin, onNavigateToAccountSync)}
    </StyledSectionDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderUnlinkContent(username: string | undefined, isGlobalAdmin: boolean, onSignOut: Handler) {
  if (!username) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-salesforce-connected-as">
        Your company is connected as
      </Trans>{' '}
      <StyledUsernameSpan>{username}</StyledUsernameSpan>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSignOut} isDisabled={!isGlobalAdmin}>
          <Trans id="settings-apps-configure-app-dialog-salesforce-unlink-button">
            Unlink Salesforce account
          </Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}

function maybeRenderLinkContent(
  username: string | undefined,
  isGlobalAdmin: boolean,
  onSignIn: HandlerOf<boolean>,
) {
  if (username) {
    return null;
  }
  return (
    <StyledButtonWrapperDiv>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={(event) => onSignIn(event.shiftKey)}
        isDisabled={!isGlobalAdmin}
      >
        <Trans id="settings-apps-configure-app-dialog-salesforce-link-button">
          Link company Salesforce account
        </Trans>
      </Button>
    </StyledButtonWrapperDiv>
  );
}

function renderAccountSyncDetails(isGlobalAdmin: boolean, onNavigateToAccountSync: Handler) {
  return (
    <>
      <StyledAccountSyncTitleDiv>
        <Trans id="settings-apps-configure-app-dialog-salesforce-account-sync-title">Account sync</Trans>
      </StyledAccountSyncTitleDiv>
      <StyledAccountSyncContentWrapperDiv>
        <StyledAccountSyncDescriptionDiv>
          <Trans id="settings-apps-configure-app-dialog-salesforce-account-sync-description">
            Syncing your accounts data will allow you to use customer information in rules, and will ensure
            your customer data is always up to date in Front.
          </Trans>
        </StyledAccountSyncDescriptionDiv>
        <StyledManageButtonDiv>
          <Button onClick={onNavigateToAccountSync} isDisabled={!isGlobalAdmin}>
            <StyledButtonContentWrapperDiv>
              <Trans id="settings-apps-configure-app-dialog-salesforce-account-sync-button">Manage</Trans>
              <Icon name="externalLinkSmall" />
            </StyledButtonContentWrapperDiv>
          </Button>
        </StyledManageButtonDiv>
      </StyledAccountSyncContentWrapperDiv>
    </>
  );
}
